import styled, { Interpolation } from '@emotion/styled'

import { colors, metrics } from 'component-library/__styling/settings'
import { breakpoint, calcRem, breakpointMax } from 'component-library/__styling/style-functions'
import { breakWords } from 'component-library/__styling/style-mixins'
import {
    SponsoredLabel,
    Props as SponsoredLabelProps,
} from 'component-library/advertising/SponsoredLabel/SponsoredLabel'
import { TextClassName as SponsoredTextClassName } from 'component-library/advertising/SponsoredLabel/SponsoredLabel.styled'
import { TextLink, TextLinkProps } from 'component-library/typography/TextLink/TextLink'
import {
    ProfileEmailButton,
    Props as ProfileEmailButtonProps,
} from 'component-library/buttons/ProfileEmailButton/ProfileEmailButton'
import { Timestamp } from 'component-library/typography/Timestamp/Timestamp'
import { Picture, ObjectFitContain } from 'component-library/content/Picture/Picture'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'
import { withClass, createPropFilter } from 'common/emotion'
import { themedValue } from 'component-library/__styling/themed-value'
import { IconPremium } from 'component-library/icons/IconPremium/IconPremium'
import {
    forEachCropBreakpoint,
    fixedRatioToBreakpointRatios,
} from 'component-library/content/Picture/utils'
import { FilterProperty } from 'csstype'
import { Theme } from '@emotion/react'
import { SrcLogoProps } from './Byline'

const bylineSourceSeparatorClassname = 'byline-source-separator'

export interface StyledBylineProps {
    isFullWidth: boolean
}

export const StyledByline = styled('address')<StyledBylineProps>({}, ({ theme, isFullWidth }) => ({
    breakWords,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    fontWeight: 400,
    fontFamily: theme.fonts.publication.byline,
    fontSize: calcRem(
        themedValue(theme, {
            sevennews: 10,
            perthnow: 16,
            thewest: 15,
        })
    ),
    color: themedValue(theme, {
        sevennews: colors.sevennews.charade,
        perthnow: colors.perthnow.greyCoal,
        thewest: colors.thewest.greyGorilla,
    }),
    lineHeight: themedValue(theme, { fallback: 1, thewest: 1.22 }),
    borderBottom: themedValue(theme, {
        sevennews: `1px solid ${colors.sevennews.alto}`,
        perthnow: `4px solid ${colors.perthnow.greyPyrite}`,
        thewest: undefined,
    }),
    paddingBottom: themedValue(theme, {
        sevennews: calcRem(metrics.sevennews.margins.md),
        perthnow: calcRem(metrics.perthnow.margins.sm),
        thewest: undefined,
    }),

    ...conditionalStyles(isFullWidth, {
        width: '100%',
    }),

    [breakpoint('xs')]: {
        flexWrap: 'nowrap',

        borderBottom: themedValue(theme, {
            sevennews: `1px solid ${colors.sevennews.alto}`,
            perthnow: `4px solid ${colors.perthnow.greyPyrite}`,
            thewest: `2px solid ${colors.thewest.greySeal}`,
        }),
        paddingBottom: themedValue(theme, {
            sevennews: calcRem(metrics.sevennews.margins.md),
            perthnow: calcRem(metrics.perthnow.margins.sm),
            thewest: calcRem(metrics.thewest.margins.md),
        }),
    },
}))

export const StyledBylineText = styled('div')(({ theme }) => ({
    ...conditionalStyles(theme.kind !== 'sevennews', {
        flexGrow: 0,
        flexShrink: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        flexBasis: 'auto',
        marginRight: 'auto',
    }),
}))

export const StyledHeadshot = styled(Picture as React.ComponentType<ObjectFitContain>)<
    ObjectFitContain
>(
    ({ image, fixedRatio = 'original' }: ObjectFitContain) => {
        let returnStyles = {}

        const breakpointRatios = fixedRatioToBreakpointRatios(fixedRatio)
        forEachCropBreakpoint(breakpointRatios, (breakpoint, ratio) => {
            const crop = image.crops[ratio]
            if (crop) {
                if (breakpoint === 'default') {
                    returnStyles = {
                        ...returnStyles,
                        width: calcRem(crop.width),
                    }
                    return
                }

                returnStyles = {
                    ...returnStyles,
                    [breakpointMax(breakpoint)]: {
                        width: calcRem(crop.width),
                    },
                }
            }
        })

        return returnStyles
    },
    ({ theme }) => ({
        // Filter is an array function, so need to be explict with types
        filter: themedValue<
            FilterProperty | undefined,
            FilterProperty | undefined,
            FilterProperty | undefined,
            FilterProperty | undefined
        >(theme, {
            fallback: 'grayscale(100%)',
            perthnow: undefined,
        }),
    })
)

export const StyledHeadshotWrapper = styled('div')({
    display: 'none',

    [breakpoint('xs')]: {
        display: 'block',
        borderRadius: '50%',
        marginRight: calcRem(10),
        overflow: 'hidden',
    },
})

export const StyledBylineTextWrap = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    overflowX: 'hidden',
    lineHeight: 'normal',

    ...conditionalStyles(theme.kind !== 'sevennews', {
        lineHeight: 1.25,
    }),
}))
StyledBylineTextWrap.displayName = 'BylineContentWrapper'

/**
 * Styled text block styles are used as an abstract set of styles which get re-used
 * for the byline text content. The author text, sponsored label, and any fallback
 * text get these styles applied.
 */
const styledTextBlockStyles: Interpolation<{ theme: Theme }>[] = [
    ({ theme }) => ({
        '&:first-child': {
            fontWeight: 600,

            '&::before': {
                content: 'none',
            },
        },
        ...conditionalStyles(theme.kind !== 'sevennews', {
            marginRight: 16,

            '&::before': {
                content: `''`,
                display: 'inline-block',
                width: 1,
                height: calcRem(13),
                transform: 'translate(-7px, 1px)',
                background: themedValue(theme, {
                    thewest: colors.thewest.greyGorilla,
                    perthnow: colors.perthnow.greyCoal,
                    sevennews: colors.sevennews.stormGrey,
                }),
            },
        }),
        ...conditionalStyles(theme.kind === 'sevennews', {
            marginRight: 14,

            [breakpointMax('xxs')]: {
                marginRight: calcRem(4),
            },
        }),
    }),
]

export const StyledBylineTextBlock = styled('span')<{ isSource?: boolean; isLive?: boolean }>(
    ...styledTextBlockStyles,

    ({ isSource, isLive, theme }) => ({
        ...conditionalStyles(isLive && (theme.kind === 'thewest' || theme.kind === 'perthnow'), {
            marginRight: calcRem(16),
        }),
        ...conditionalStyles(isSource !== true && theme.kind === 'sevennews', {
            [breakpointMax('xxs')]: {
                display: 'block',
            },
        }),
    })
)

export const StyledBylinePosition = styled('span')(...styledTextBlockStyles, ({ theme }) => ({
    marginLeft: themedValue(theme, { sevennews: calcRem(8), fallback: undefined }),
}))

export const StyledBylineTextWrapper = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    overflowX: 'hidden',
})
StyledBylineTextWrapper.displayName = 'BylineText'

export const StyledBylineAuthor = styled(TextLink)<TextLinkProps>(
    ...(styledTextBlockStyles as any),
    ({ theme }) => {
        if (theme.kind !== 'perthnow') {
            return {
                textDecoration: 'none',

                '&:hover, &:focus': {
                    textDecoration: 'underlined',
                },
            }
        }
        return {}
    },
    ({ theme }) => ({
        ...conditionalStyles(theme.kind === 'sevennews', {
            [breakpointMax('xxs')]: {
                display: 'block',
            },
        }),
    })
)

export const StyledSponsoredLabel = styled(SponsoredLabel)<SponsoredLabelProps>(
    ...styledTextBlockStyles,
    {
        display: 'inline-block',
        // The children of the sponsored label need to have their vertical alignment reset - otherwise they sit awkwardly
        [`& .${SponsoredTextClassName}`]: {
            verticalAlign: 'unset',
            // perth now has a margin-top set - that's why we reset it
            marginTop: 0,
        },
    }
)

export const StyledTimestamp = styled(Timestamp)(({ theme }) => ({
    fontFamily: theme.fonts.publication.byline,
    marginRight: calcRem(8),
    padding: themedValue(theme, { sevennews: `${calcRem(5)} 0`, fallback: undefined }),
    color: themedValue(theme, {
        thewest: colors.thewest.greyGorilla,
        perthnow: colors.perthnow.greyCoal,
        sevennews: colors.sevennews.stormGrey,
    }),

    ...conditionalStyles(theme.kind === 'thewest', {
        // Sets numbers to render at the same size - fonts must have number glyphs included that support this.
        // Currently no support exists for our web verison gauadian egyptian text - but have kept this here for the future.
        fontVariantNumeric: 'tabular-nums',
        fontSize: calcRem(14),
    }),
}))

// For The West
export const StyledProfileEmailButton = styled(ProfileEmailButton)<ProfileEmailButtonProps>({
    alignSelf: 'center',
    marginTop: calcRem(metrics.thewest.margins.sm),
    flexShrink: 0,

    [breakpoint('xs')]: {
        marginTop: 0,
    },
})

interface SourceLogoProps {
    maxWidth?: number
}

export const StyledSourceLogo = styled('img', {
    shouldForwardProp: createPropFilter<SrcLogoProps>()(['maxWidth']),
})<SourceLogoProps>(({ maxWidth }) => ({
    maxWidth: maxWidth || calcRem(70),
    marginTop: 3,
}))

export const StyledSourceWrapper = styled('div')({
    display: 'inline-block',
    width: 'auto',
    fontWeight: 500,
    fontStyle: 'italic',
    padding: 0,
    verticalAlign: 'middle',
    marginRight: calcRem(8),
})

export const StyledMiddleDot = withClass(bylineSourceSeparatorClassname)(
    styled('span')(({ theme }) => ({
        '&:not(:first-child)': {
            [breakpoint('xxs')]: {
                margin: 0,

                '&::before': {
                    content: "''",
                    display: 'inline-block',
                    transform: 'translate(-7px, -1px)',
                    width: calcRem(3),
                    height: calcRem(3),
                    borderRadius: calcRem(1.5),
                    backgroundColor: themedValue(theme, {
                        sevennews: colors.sevennews.charade,
                        perthnow: colors.perthnow.greyCoal,
                        thewest: colors.thewest.greyGorilla,
                    }),
                },
            },
        },
    }))
)

export const StyledLabelWrapper = styled('div')(({ theme }) => ({
    overflow: 'hidden',
    paddingLeft: calcRem(8),
    transform: 'translate(-8px)',
    display: 'flex',

    ...conditionalStyles(theme.kind === 'thewest' || theme.kind === 'perthnow', {
        '&::before': {
            content: `''`,
            display: 'inline-block',
            width: 1,
            height: calcRem(13),
            transform: 'translate(-7px, 1px)',
            background: themedValue(theme, {
                thewest: colors.thewest.greyGorilla,
                perthnow: colors.perthnow.greyCoal,
                sevennews: colors.sevennews.charade,
            }),
        },
    }),
}))

export const StyledMetaDataWrapper = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'center',
    overflowX: 'hidden',
    overflowY: 'auto',
})
export const StyledPremiumWrapper = styled('span')(({ theme }) => ({
    display: 'block',

    '& > *': {
        marginRight: 16,
    },

    '&::before': {
        content: `''`,
        display: 'inline-block',
        width: 1,
        height: calcRem(13),
        transform: 'translate(-3px, -1px)',
        background: themedValue(theme, {
            sevennews: colors.sevennews.charade,
            perthnow: colors.perthnow.greyCoal,
            thewest: colors.thewest.greyGorilla,
        }),
    },
}))

export interface StyledPremiumIconProps {
    width: number
    height: number
}
export const StyledPremiumIcon = styled(IconPremium)<StyledPremiumIconProps>(
    ({ width, height }) => ({
        width,
        height,
    })
)
